import {
  Alert,
  Flex,
  FlexItem,
  Tab,
  Tabs,
  TabTitleText,
} from '@patternfly/react-core';
import {useState} from 'react';
import {DrawerContentType} from 'src/routes/RepositoryDetails/Types';
import DeleteRepository from './DeleteRepository';
import Permissions from './Permissions';
import Notifications from './Notifications';
import Visibility from './Visibility';
import {RepositoryStateForm} from './RepositoryState';
import {RepositoryDetails} from 'src/resources/RepositoryResource';
import {useQuayConfig} from 'src/hooks/UseQuayConfig';
import RepositoryAutoPruning from 'src/routes/RepositoryDetails/Settings/RepositoryAutoPruning';
import {useOrganization} from 'src/hooks/UseOrganization';

export default function Settings(props: SettingsProps) {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const config = useQuayConfig();
  const {isUserOrganization} = useOrganization(props.org);

  const tabs = [
    {
      name: 'User and robot permissions',
      id: 'userandrobotpermissions',
      content: (
        <Permissions
          org={props.org}
          repo={props.repo}
          setDrawerContent={props.setDrawerContent}
        />
      ),
    },
    ...(config?.features?.AUTO_PRUNE && props.repoDetails?.can_write
      ? [
          {
            name: 'Repository Auto-Prune Policies',
            id: 'repositoryautoprunepolicies',
            content: (
              <RepositoryAutoPruning
                organizationName={props.org}
                repoName={props.repo}
                isUser={isUserOrganization}
              />
            ),
          },
        ]
      : []),
    {
      name: 'Events and notifications',
      id: 'eventsandnotifications',
      content: (
        <Notifications
          org={props.org}
          repo={props.repo}
          setDrawerContent={props.setDrawerContent}
        />
      ),
    },
    ...(config?.features?.REPO_MIRROR
      ? [
          {
            name: 'Repository state',
            id: 'repositorystate',
            content: (
              <RepositoryStateForm
                org={props.org}
                repo={props.repo}
                repoDetails={props.repoDetails}
              />
            ),
          },
        ]
      : []),
    {
      name: 'Repository visibility',
      id: 'repositoryvisiblity',
      content: (
        <Visibility
          org={props.org}
          repo={props.repo}
          repoDetails={props.repoDetails}
        />
      ),
    },
    {
      name: <div style={{color: 'red'}}>Delete Repository</div>,
      id: 'deleterepository',
      content: <DeleteRepository org={props.org} repo={props.repo} />,
    },
  ];

  return (
    <Flex flexWrap={{default: 'nowrap'}}>
      <FlexItem>
        <Tabs
          activeKey={activeTabIndex}
          onSelect={(e, index: number) => setActiveTabIndex(index)}
          isVertical
          aria-label="Repository Settings Tabs"
          role="region"
        >
          {tabs.map((tab, tabIndex) => (
            <Tab
              key={tab.id}
              eventKey={tabIndex}
              title={<TabTitleText>{tab.name}</TabTitleText>}
              data-testid={`settings-tab-${tab.id}`}
            />
          ))}
        </Tabs>
      </FlexItem>
      <FlexItem
        alignSelf={{default: 'alignSelfCenter'}}
        style={{padding: '20px', width: '100%'}}
      >
        {props.repoDetails?.state === 'ORG_MIRROR' && (
          <Alert
            isInline
            variant="info"
            title="This repository is managed by organization-level mirroring. Some settings may be restricted."
            className="pf-v6-u-mb-md"
            data-testid="org-mirror-repo-settings-banner"
          />
        )}
        {tabs.at(activeTabIndex).content}
      </FlexItem>
    </Flex>
  );
}

interface SettingsProps {
  org: string;
  repo: string;
  repoDetails: RepositoryDetails;
  setDrawerContent: (content: DrawerContentType) => void;
}
